import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import XIcon from '@/assets/icons/x.svg';

type Props = {
  children: React.ReactNode;
  modal: boolean;
  headerText?: string;
  setModal: (modal: boolean) => void;
  className?: string;
};

export default function Modal({
  modal,
  setModal,
  headerText,
  children,
  className = 'max-w-lg',
}: Props) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setModal(false);
      }
    };

    if (modal) {
      window.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    } else {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    }

    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [modal, setModal]);

  if (!modal || !mounted) return null;

  return createPortal(
    <div
      id="modal-root"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
    >
      {/* Backdrop overlay with translucent dark/light opacity & blur */}
      <div
        className="fixed inset-0 bg-slate-900/60 dark:bg-black/75 backdrop-blur-sm transition-opacity"
        onClick={() => setModal(false)}
        aria-hidden="true"
      />

      {/* Modal Dialog Card */}
      <div
        className={clsx(
          'relative z-10 w-full rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-2xl border border-slate-200/80 dark:border-gray-800 transition-all my-auto',
          className
        )}
      >
        {headerText && (
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100 dark:border-gray-800">
            <h2 className="font-semibold text-lg text-slate-900 dark:text-white">
              {headerText}
            </h2>
          </div>
        )}

        <button
          type="button"
          onClick={() => setModal(false)}
          className="absolute top-4 right-4 inline-flex items-center justify-center p-1.5 rounded-full text-slate-400 hover:text-slate-700 dark:text-gray-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-800 transition"
          aria-label="Close modal"
        >
          <XIcon className="h-5 w-5" />
        </button>

        {children}
      </div>
    </div>,
    document.body
  );
}
